import { ChangeDetectionStrategy, Component, forwardRef, Input, OnInit } from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import {
    CoverageIdEnum,
    CoverageSpecificationInterface,
    NewModal,
    PurchasedProductInterface,
    RelationInterface,
} from 'outshared-lib';
import { YearRewardModal, YearRewardModalModule } from '../../modals';
import { CoverageControl, CoverageGroupAbstract } from './classes';
import { CoverageCollectionLabelEnum, CoverageGroupTypeEnum } from './enums';
import { CoverageControlCollectionInterface, InsuranceCoverageControlFormValuesInterface } from './interfaces';
import { flattenToArray, isArrayEqual } from '@app/shared/utilities/array-utilities';
import { NgClass, NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { WucCardModule, WucIconUiModule } from '@inshared/website-ui-components';
import {
    CoverageCardComponent,
    CoverageDefaultComponent,
    CoverageDisabledComponent,
    CoverageDynamicComponent,
    CoverageImplicitComponent,
    CoverageRadioComponent,
    CoverageTableComponent,
    CoverageToggleComponent,
    PremiumSummaryComponent,
} from './components';

@Component({
    selector: 'ins-insurance-coverage-control',
    templateUrl: './insurance-coverage-control.component.html',
    styleUrls: ['./insurance-coverage-control.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => InsuranceCoverageControlComponent),
        multi: true,
    }],
    standalone: true,
    imports: [
        ReactiveFormsModule,
        NgFor,
        NgIf,
        NgClass,
        NgSwitch,
        NgSwitchDefault,
        NgSwitchCase,
        WucCardModule,
        WucIconUiModule,
        PremiumSummaryComponent,
        YearRewardModalModule,
        CoverageDefaultComponent,
        CoverageCardComponent,
        CoverageDisabledComponent,
        CoverageImplicitComponent,
        CoverageRadioComponent,
        CoverageTableComponent,
        CoverageDynamicComponent,
        CoverageToggleComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsuranceCoverageControlComponent implements ControlValueAccessor, OnInit {

    @Input() public purchasedProduct!: PurchasedProductInterface;
    @Input() public relation!: RelationInterface;

    public coverageControl: CoverageControl;
    public purchasedCoverageIds: CoverageIdEnum[];
    public purchasedCoverages: CoverageSpecificationInterface[];
    public form: UntypedFormGroup;
    public coverageCollectionLabelEnum: typeof CoverageCollectionLabelEnum;
    public coverageGroupTypeEnum: typeof CoverageGroupTypeEnum;
    public detailsExpand: boolean = false;

    constructor(
        private modalService: NewModal,
    ) {
        this.coverageCollectionLabelEnum = CoverageCollectionLabelEnum;
        this.coverageGroupTypeEnum = CoverageGroupTypeEnum;
    }

    public ngOnInit(): void {
        const coverageControl = new CoverageControl(this.purchasedProduct, this.relation);
        const coverageIds = this.getPurchasedCoverageIds(this.purchasedProduct);

        this.setCoverageControl(coverageControl);
        this.createForm(coverageControl);
        this.calculatePremium(coverageIds, this.purchasedProduct);

        this.form.valueChanges
            .subscribe((values: InsuranceCoverageControlFormValuesInterface) => this.formValuesChanged(values));
    }

    public writeValue(): void {
        return; // The values are patched in createForm
    }

    public registerOnChange(fn): void {
        this.propagateChange = fn;
    }

    public registerOnTouched(fn): void {
        this.propagateTouch = fn;
    }

    public getInfo(): boolean {
        this.modalService.open(YearRewardModal);
        return false;
    }

    public propagateChange: (value: any) => void = () => {
        return;
    }

    public propagateTouch = () => {
        return;
    }

    private formValuesChanged(values: InsuranceCoverageControlFormValuesInterface): void {
        const coverageIds = flattenToArray<InsuranceCoverageControlFormValuesInterface, CoverageIdEnum>(values);

        if (!isArrayEqual(coverageIds, this.purchasedCoverageIds)) {
            this.purchasedCoverageIds = coverageIds;

            this.propagateChange(this.purchasedCoverageIds);
            this.calculatePremium(this.purchasedCoverageIds, this.purchasedProduct);
        }
    }

    private setCoverageControl(coverageControl: CoverageControl): void {
        this.coverageControl = coverageControl;
    }

    private createForm(coverageControl: CoverageControl): void {
        const formGroup = {};
        const groups: CoverageGroupAbstract[] = this.getAllGroupsFromContainers(coverageControl.collections);

        groups.forEach((group: CoverageGroupAbstract) => {
            const purchasedCoverageIds: CoverageIdEnum[] = this.getPurchasedCoverageIdsFromGroup(group);
            const validators = [];

            if (group.required) {
                validators.push(Validators.required);
            }

            formGroup[group.id] = new FormControl(purchasedCoverageIds, validators);
        });

        this.form = new UntypedFormGroup(formGroup);
        this.purchasedCoverageIds = flattenToArray<InsuranceCoverageControlFormValuesInterface, CoverageIdEnum>(this.form.value);
    }

    private getPurchasedCoverageIdsFromGroup(group: CoverageGroupAbstract): CoverageIdEnum[] {
        const coverageIds: CoverageIdEnum[] = [];

        // Filter purchased coverageIds
        group.inputs
            .filter((input) => Boolean(input.purchased.current || input.purchased.future))
            .map((input) => coverageIds.push(...input.coverageIds));

        // Filter duplicates
        return [...new Set(coverageIds)];
    }

    private getPurchasedCoverageIds(purchasedProduct: PurchasedProductInterface): CoverageIdEnum[] {
        const coverages = [
            ...purchasedProduct.coverage.baseCoverages,
            ...purchasedProduct.coverage.additionalCoverages,
        ];

        return coverages
            .filter((coverage) => Boolean(coverage.purchasedCoverage || coverage.purchasedCoverageFuture))
            .map((coverage) => coverage.coverageId);
    }

    private getAllGroupsFromContainers(
        collections: CoverageControlCollectionInterface[],
    ): CoverageGroupAbstract[] {
        const groups: CoverageGroupAbstract[] = [];
        collections.map((collection) => collection.groups.map((group) => groups.push(group)));
        return groups;
    }

    private calculatePremium(
        coverageIds: CoverageIdEnum[],
        purchasedProduct: PurchasedProductInterface,
    ): void {
        // let _price = 0;
        // let _discount = 0;
        // let _priceDiscount = 0;
        const coverages = [
            ...purchasedProduct.coverage.baseCoverages,
            ...purchasedProduct.coverage.additionalCoverages,
        ];
        this.purchasedCoverages = coverages
            .filter((coverage) => coverageIds.includes(coverage.coverageId));

        // this.purchasedCoverages.forEach((purchasedCoverage) => {
        //     const {premium, discount, premiumIncludingDiscount} = purchasedCoverage;
        //
        //     _price = _price + premium?.premiumIncludingPremiumTax;
        //     _discount = _discount + discount?.premiumIncludingPremiumTax;
        //     _priceDiscount = _priceDiscount + premiumIncludingDiscount?.premiumIncludingPremiumTax;
        // });
        //
        // this.price = _price ?? 0;
        // this.discount = _discount ?? 0;
        // this.priceDiscount = _priceDiscount ?? 0;
        //
        // this.purchasedProduct = {
        //     ...purchasedProduct,
        //     coverage: {
        //         ...purchasedProduct.coverage,
        //     }
        // };
    }
}
